import React, { useState, useEffect } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";

function rowId(m) {
  return m?.Id ?? m?.id;
}

/** Card toggle — used for Internal access (PH Admin, Zephyr). */
function InternalRoleCard({ label, color, checked, onChange, disabled }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={`flex-1 text-left rounded-xl border-2 p-3 transition-all ${
        checked ? "shadow-sm" : "border-slate-200 bg-white hover:border-slate-300"
      }`}
      style={checked ? { borderColor: color, backgroundColor: `${color}12` } : {}}
    >
      <div className="flex items-center gap-2.5">
        <div
          className="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors"
          style={
            checked
              ? { borderColor: color, backgroundColor: color }
              : { borderColor: "#cbd5e1", backgroundColor: "transparent" }
          }
        >
          {checked && (
            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 12 12">
              <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </div>
        <p className="text-sm font-semibold" style={checked ? { color } : { color: "#475569" }}>
          {label}
        </p>
      </div>
    </button>
  );
}

/** Pill toggle — used for External portals and Status. Rounded-full track, circle thumb. */
function PillToggleRow({ label, checked, onChange, disabled }) {
  return (
    <div
      className={`flex items-center justify-between px-3 py-2.5 border-b border-slate-100 last:border-b-0 select-none transition-colors ${
        disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-slate-50"
      }`}
      onClick={() => !disabled && onChange(!checked)}
    >
      <span className="text-sm font-medium text-slate-700">{label}</span>
      {/* Track: rounded-full (pill), thumb: rounded-full (circle) */}
      <div
        className="relative flex-shrink-0 rounded-full transition-colors duration-200"
        style={{ width: 40, height: 24, backgroundColor: checked ? "#16a34a" : "#dc2626" }}
      >
        <div
          className="absolute bg-white rounded-full shadow transition-transform duration-200"
          style={{ width: 16, height: 16, top: 4, transform: checked ? "translateX(20px)" : "translateX(4px)" }}
        />
      </div>
    </div>
  );
}

// ── Role definitions ─────────────────────────────────────────────────────────

const INTERNAL_ROLES = [
  { key: "IsPHAdmin",           label: "PH Admin",    color: "#2563eb" },
  { key: "IsZephyrAdmin",       label: "Zephyr Admin", color: "#38184f" },
  { key: "is_conferences_user", label: "Conferences",  color: "#FFAF3C" },
];

// Add new external portals here — appear automatically in the list
const EXTERNAL_ROLES = [
  { key: "IsHubMedAdmin",  label: "HubMed Admin"  },
  { key: "IsHolaAdmin",    label: "Hola Admin"    },
  { key: "IsMedmateAdmin", label: "Medmate Admin" },
];

// Opt-ins that auto-assign work to this person, rather than granting access.
const AUTO_TAG_ROLES = [
  {
    key: "receives_checkin_tasks",
    label: "Trial check-in tasks",
    hint: "Auto-assigned the week 1/2/3 check-in tasks when a pharmacy starts a Quda or MediDrop trial.",
  },
];

const ALL_ROLE_KEYS = [...INTERNAL_ROLES, ...EXTERNAL_ROLES, ...AUTO_TAG_ROLES].map(r => r.key);
const EMPTY_ROLES   = Object.fromEntries(ALL_ROLE_KEYS.map(k => [k, false]));

// Suggested accessible palette for Task Manager staff colour coding.
const TASK_COLOR_PALETTE = ['#2563EB', '#0D9488', '#EA580C', '#7C3AED', '#D97706', '#DB2777', '#0891B2', '#65A30D', '#DC2626', '#4F46E5'];
const normHex = (v) => (v ? String(v).toUpperCase() : null);

// ── Component ────────────────────────────────────────────────────────────────

const EditGroupManagerModal = ({ isOpen, onClose, manager, onSaved }) => {
  const [roles, setRoles]     = useState(EMPTY_ROLES);
  const [isActive, setIsActive] = useState(true);
  const [taskColor, setTaskColor] = useState(null);
  const [slackUserID, setSlackUserID] = useState("");
  const [slackUsers, setSlackUsers] = useState([]);
  const [slackLoading, setSlackLoading] = useState(false);
  const [slackConfigured, setSlackConfigured] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!isOpen || !manager) return;
    setRoles(Object.fromEntries(ALL_ROLE_KEYS.map(k => [k, !!manager[k]])));
    setIsActive(manager.IsActive !== false);
    setTaskColor(normHex(manager.task_color));
    setSlackUserID(manager.slackUserID || "");
    setError(null);
    setSaving(false);
  }, [isOpen, manager]);

  // Load the Slack workspace member list once the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    setSlackLoading(true);
    phappsRequest(API_PATHS.GROUP_MANAGERS_SLACK_USERS, "GET")
      .then((res) => {
        setSlackUsers(res?.users ?? []);
        setSlackConfigured(res?.configured !== false);
      })
      .catch(() => { setSlackUsers([]); setSlackConfigured(false); })
      .finally(() => setSlackLoading(false));
  }, [isOpen]);

  if (!isOpen || !manager) return null;

  const toggle = (key) => (val) => setRoles(r => ({ ...r, [key]: val }));

  const dirty =
    ALL_ROLE_KEYS.some(k => roles[k] !== !!manager[k]) ||
    isActive !== (manager.IsActive !== false) ||
    taskColor !== normHex(manager.task_color) ||
    (slackUserID || "") !== (manager.slackUserID || "");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!dirty) { onClose(); return; }
    setError(null);
    setSaving(true);
    try {
      const body = { Id: rowId(manager), ...roles, IsActive: !!isActive, task_color: taskColor, slackUserID: slackUserID || null };
      const res = await phappsRequest(API_PATHS.GROUP_MANAGERS_UPDATE, "PUT", body);
      onSaved?.(res);
      onClose();
    } catch (err) {
      setError(err.message || "Update failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto p-4">
      <div className="bg-white rounded-2xl w-full max-w-md mx-auto my-8 shadow-xl">

        {/* Header — just the manager's name */}
        <div className="px-6 py-5 border-b border-slate-100">
          <h2 className="text-xl font-bold text-slate-900">
            {manager.Name || manager.Email || `Manager #${rowId(manager)}`}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">

          {/* Internal — PH Admin + Zephyr, separate from external portals */}
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Internal access</p>
            <div className="flex gap-2">
              {INTERNAL_ROLES.map(r => (
                <InternalRoleCard
                  key={r.key}
                  label={r.label}
                  color={r.color}
                  checked={roles[r.key]}
                  onChange={toggle(r.key)}
                  disabled={saving}
                />
              ))}
            </div>
          </div>

          {/* External portals — scalable list */}
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">External portals</p>
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              {EXTERNAL_ROLES.map(r => (
                <PillToggleRow
                  key={r.key}
                  label={r.label}
                  checked={roles[r.key]}
                  onChange={toggle(r.key)}
                  disabled={saving}
                />
              ))}
            </div>
          </div>

          {/* Auto-tagging — who work gets assigned to automatically */}
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Auto-tagged in</p>
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              {AUTO_TAG_ROLES.map(r => (
                <PillToggleRow
                  key={r.key}
                  label={r.label}
                  checked={roles[r.key]}
                  onChange={toggle(r.key)}
                  disabled={saving}
                />
              ))}
            </div>
            {AUTO_TAG_ROLES.map(r => (
              <p key={r.key} className="text-xs text-slate-400 mt-1.5">{r.hint}</p>
            ))}
          </div>

          {/* Task Manager colour */}
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Task Manager colour</p>
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="flex items-center gap-3 mb-3">
                <span
                  className="w-9 h-9 rounded-full ring-2 ring-white shadow flex-shrink-0 border border-slate-200"
                  style={{ backgroundColor: taskColor || '#E5E7EB' }}
                />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-700">
                    {taskColor || 'No colour (grey)'}
                  </p>
                  <p className="text-xs text-slate-400">Used to colour this staff member's tasks on the weekly board.</p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {TASK_COLOR_PALETTE.map((c) => {
                  const selected = taskColor === c;
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setTaskColor(c)}
                      disabled={saving}
                      title={c}
                      className={`w-7 h-7 rounded-full ring-2 transition hover:scale-110 ${selected ? 'ring-slate-800' : 'ring-white'} shadow`}
                      style={{ backgroundColor: c }}
                    >
                      {selected && (
                        <svg className="w-4 h-4 mx-auto text-white" fill="none" viewBox="0 0 12 12">
                          <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </button>
                  );
                })}

                {/* Custom colour */}
                <label
                  title="Custom colour"
                  className="w-7 h-7 rounded-full ring-2 ring-white shadow flex items-center justify-center cursor-pointer bg-gradient-to-br from-pink-500 via-amber-400 to-sky-500 relative overflow-hidden"
                >
                  <input
                    type="color"
                    value={taskColor || '#2563EB'}
                    onChange={(e) => setTaskColor(normHex(e.target.value))}
                    disabled={saving}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                  />
                  <svg className="w-3.5 h-3.5 text-white drop-shadow" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                  </svg>
                </label>

                {taskColor && (
                  <button
                    type="button"
                    onClick={() => setTaskColor(null)}
                    disabled={saving}
                    className="ml-1 text-xs font-medium text-slate-500 hover:text-slate-700 underline"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Slack user mapping */}
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Slack user</p>
            <Autocomplete
              freeSolo
              size="small"
              loading={slackLoading}
              options={slackUsers}
              value={slackUsers.find((u) => u.id === slackUserID) || (slackUserID ? slackUserID : null)}
              getOptionLabel={(o) =>
                typeof o === "string"
                  ? o
                  : `${o.real_name || o.name || o.id}${o.email ? ` (${o.email})` : ""}`
              }
              isOptionEqualToValue={(o, v) => (o?.id ?? o) === (v?.id ?? v)}
              filterOptions={(opts, state) => {
                const q = state.inputValue.trim().toLowerCase();
                if (!q) return opts.slice(0, 50);
                return opts
                  .filter((o) =>
                    `${o.real_name} ${o.name} ${o.email || ""} ${o.id}`.toLowerCase().includes(q)
                  )
                  .slice(0, 50);
              }}
              onChange={(_, val) => {
                if (!val) setSlackUserID("");
                else if (typeof val === "string") setSlackUserID(val.trim());
                else setSlackUserID(val.id);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={slackConfigured ? "Search by name or email…" : "Paste Slack member ID (Uxxxxxxxx)"}
                  helperText={
                    slackConfigured
                      ? (slackUserID ? `Linked: ${slackUserID}` : "Used for @mentions and DMs in the workflow channel.")
                      : "Slack member list unavailable — paste the member ID (Uxxxxxxxx) manually."
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {slackLoading ? <CircularProgress size={15} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </div>

          {/* Status */}
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Status</p>
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <PillToggleRow
                label="Active"
                checked={isActive}
                onChange={setIsActive}
                disabled={saving}
              />
            </div>
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 text-red-700 text-sm px-3 py-2 border border-red-100">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 text-sm font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !dirty}
              className="px-5 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-semibold"
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditGroupManagerModal;
